/**
 * Encounter form shell.
 *
 * The single component that mount points (`ModalProvider`,
 * `EncounterCompletionContent`, `IntakeCompletionContent`) render. Resolves
 * `patient` and (optionally) `encounter` from props or URL, then delegates
 * the rendered form to {@link EncounterTypeDispatcher}. The shell forwards
 * the existing `EncounterFormRef` (`checkProtection` / `markAsClean`) so
 * existing modal protection logic is unchanged.
 *
 * Phase 3 of the encounter form decomposition: the shell is the new mount
 * boundary, but the dispatcher still delegates every type to the legacy
 * `EncounterForm`. Subsequent phases route specific types to typed forms.
 *
 * @agentic connections
 */

import { forwardRef } from "react";
import type { Encounter } from "@medplum/fhirtypes";
import type { EncounterActivityContext } from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";
import { useStableParams } from "@/hooks/common/useStableParams";
import { EncounterTypeDispatcher } from "./EncounterTypeDispatcher";

/**
 * Public ref shape exposed by the shell to mount points (e.g.
 * `ModalProvider`). Lets the parent run the form's protection checks
 * before closing the modal and mark the form as clean after a successful
 * save.
 *
 * @agentic connections
 */
export type EncounterFormRef = {
  checkProtection: () => Promise<boolean>;
  markAsClean: () => void;
};

/**
 * Props for the shell. Mirrors the public surface of `EncounterForm`. The
 * shell resolves `patientId` from URL parameters when neither
 * `propPatientId` nor `propEncounter` is provided so the modal-vs-route
 * fallback behaviour from the legacy form continues to work.
 *
 * @agentic connections
 */
export type EncounterFormShellProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Encounter form shell. Resolves `patientId` (prop or URL) and forwards to
 * the dispatcher. Phase 13 implementation: every encounter type routes to
 * a typed form via the dispatcher; the legacy monolith has been removed.
 *
 * @agentic connections
 */
export const EncounterFormShell = forwardRef<
  EncounterFormRef,
  EncounterFormShellProps
>(function EncounterFormShell(
  {
    patientId: propPatientId,
    encounter,
    onSuccess,
    onCancel,
    activityContext,
    restrictToEncounterTypes,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    nextActivityAppliedRule,
    isResolvingNextTaskOwner = false,
  }: EncounterFormShellProps,
  ref,
) {
  const { patientId: urlPatientId } = useStableParams();
  const effectivePatientId = propPatientId || urlPatientId;

  return (
    <EncounterTypeDispatcher
      ref={ref}
      patientId={effectivePatientId}
      encounter={encounter}
      onSuccess={onSuccess}
      onCancel={onCancel}
      activityContext={activityContext}
      restrictToEncounterTypes={restrictToEncounterTypes}
      nextActivityLabel={nextActivityLabel}
      nextActivityDefaultOwner={nextActivityDefaultOwner}
      nextTaskOwnerOverride={nextTaskOwnerOverride}
      onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
      nextActivityAppliedRule={nextActivityAppliedRule}
      isResolvingNextTaskOwner={isResolvingNextTaskOwner}
    />
  );
});
